import React from "react";
import { FC } from "react";
import { makeStyles } from "@material-ui/core";
import { Theme } from "@material-ui/core";
import { FormRenderProps } from "react-final-form";
import { Field } from "react-final-form";
import Typography from "@material-ui/core/Typography";
import Loading from "../../../../components/Loading";
import Autocomplete from "../../../../components/Autocomplete";
import BootstrapInput from "../../../../components/BootstrapInput";
import Button from "../../../../components/Button";
import IconButton from "@material-ui/core/IconButton";
import useServiceQueries from "../../hooks/useServiceQueries";
import { useQuery } from "react-query";
import { mutateErrors } from "../../../../utils";
import Error from "../../../../components/Error";
import FontIcon from "../../../../components/FontIcon";
import clsx from "clsx";
import CheckBoxInput from "../../../../components/CheckBoxInput";

const useStyles = makeStyles((theme: Theme) => ({
  formControl: {
    marginBottom: theme.spacing(3),
  },
  root: {
    maxWidth: 300,
    margin: "0 auto",
  },
  groupRadio: {
    display: "flex",
    gap: "18px",
  },
  link: {
    marginRight: 8,
    cursor: "pointer",
  },
}));

const ServiceForm: FC<FormRenderProps> = ({
  form,
  initialValues,
  handleSubmit,
  submitError,
  submitting,
  ...props
}) => {
  const classes = useStyles();
  const [src, setSrc] = React.useState<any>(
    initialValues ? initialValues.image : null,
  );
  const { fetchCategories } = useServiceQueries();
  const { isLoading, isFetching, isError, error, data } = useQuery<any>(
    "service_categories",
    fetchCategories,
  );

  if (isLoading || isFetching) return <Loading />;

  if (isError) {
    const { message } = mutateErrors(error);
    return <Error text={message} />;
  }

  return (
    <form onSubmit={handleSubmit}>
      {submitError && <Typography color="error">{submitError}</Typography>}
      {submitting && <Loading />}
      <Typography component="div" className={classes.root}>
        <Typography component="div" className={classes.formControl}>
          <Field
            fullWidth
            name="category"
            label="Category"
            options={data && data.data}
            placeholder="Category"
            component={Autocomplete}
          />
        </Typography>
        <Typography component="div" className={classes.formControl}>
          <Field
            fullWidth
            type="text"
            name="title"
            label="Title"
            placeholder="Title"
            component={BootstrapInput}
          />
        </Typography>

        <Typography
          component="div"
          className={clsx(classes.formControl, classes.groupRadio)}
        >
          <label>
            <span className={classes.link}>Link with details page</span>
            <Field
              name={`isWithPage`}
              type="checkbox"
              component={(props) => {
                return (
                  <CheckBoxInput
                    input={props.input}
                    checked={initialValues?.isWithPage}
                    size="small"
                  />
                );
              }}
            />
          </label>
        </Typography>
        <Typography component="div" className={classes.formControl}>
          <Field
            fullWidth
            type="text"
            name="link"
            label="Link"
            placeholder="Link"
            component={BootstrapInput}
          />
        </Typography>
        <Typography component="div" className={classes.formControl}>
          <Field
            rows={4}
            multiline
            fullWidth
            type="textarea"
            name="description"
            label="Description"
            placeholder="Description"
            component={BootstrapInput}
          />
        </Typography>

        <Typography component="div" className={classes.formControl}>
          <div>
            <label style={{ minWidth: 180 }}>
              <Button component="span" variant="outlined">
                Choose image
              </Button>
              <input
                type="file"
                value={""}
                name="image"
                accept="image/*"
                style={{ display: "none" }}
                onChange={(e) => {
                  if (e.target.files && e.target.files[0]) {
                    form.change("image", e.target.files[0]);
                    const reader = new FileReader();
                    reader.onload = function (e1) {
                      // @ts-ignore
                      setSrc(e1.target.result);
                    };
                    reader.readAsDataURL(e.target.files[0]);
                  }
                }}
              />
            </label>
            {src && (
              <div
                style={{
                  position: "relative",
                  width: "50%",
                }}
              >
                <img src={src} alt="Hello" style={{ width: "100%" }} />
                <IconButton
                  style={{
                    position: "absolute",
                    right: 5,
                    top: 5,
                    background: "rgba(255, 255, 255, 0.5)",
                  }}
                  onClick={async () => {
                    setSrc(null);
                    form.change("image", null);
                  }}
                >
                  <FontIcon
                    fontSize={"small"}
                    color={"error"}
                    name={"delete"}
                  />
                </IconButton>
              </div>
            )}
          </div>
        </Typography>

        <Typography
          align="center"
          component="div"
          className={classes.formControl}
        >
          <Button
            type="submit"
            color="secondary"
            variant="contained"
            disabled={submitting}
          >
            Save
          </Button>
        </Typography>
      </Typography>
    </form>
  );
};

export default React.memo(ServiceForm);
